import { useUser, withPageAuthRequired } from '@auth0/nextjs-auth0';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import Account from '../../components/settings/account';
import Locations from '../../components/settings/locations';
import Notifications from '../../components/settings/notifications';
import SettingsSidebar from '../../components/settings/settingsSidebar';
import { LocationEnum } from '../../utils/enums';
import getConfirmedClassicURL from '../../utils/helpers/getConfirmedClassicURL';
import { UserInfo } from '../../utils/interfaces';
import {
  LocationSearchData,
  LocationsList,
  mailOption,
  settingPage,
  TimeZone,
} from '../../utils/types';
import { NextPage } from 'next';
import { Layout } from '../../components/shared/layout/layout';

const settingsPages: settingPage[] = [
  {
    name: 'Account',
    href: '/user/account',
    key: 'account',
    selected: false,
  },
  {
    name: 'Places & Platforms',
    href: '/user/locations',
    key: 'locations',
    selected: false,
  },
  {
    name: 'Notifications',
    href: '/user/notifications',
    key: 'notifications',
    selected: false,
  },
  // {
  //   name: 'Advanced Settings (May require login)',
  //   href: '/redirect?redirect=/user/emailOptions&classic=true',
  //   key: 'advanced',
  //   selected: false,
  // },
  //Hidden until implemented on react / next gen
  // {
  //   name: 'Integrations (Classic)',
  //   href: '/user/integrations',
  //   key: 'integrations',
  //   selected: false,
  // },
  // {
  //   name: 'Templates (Classic)',
  //   href: '/user/templates',
  //   key: 'templates',
  //   selected: false,
  // },
];

type Props = {
  pages: settingPage[];
  selectedPage: settingPage | undefined;
  timeZones: TimeZone[];
  hostName: string;
};

const Settings: NextPage<Props> = ({
  pages,
  selectedPage,
  timeZones,
  hostName,
}) => {
  const [locationParams, setLocationParams] = useState<
    LocationSearchData | undefined
  >({
    LocationTypes: [
      LocationEnum.PersonalPhysical,
      LocationEnum.PersonalVirtual,
      LocationEnum.TeamPhysical,
      LocationEnum.TeamVirtual,
    ],
    NameFilter: '',
    PageNumber: 0,
    ResultsPerPage: 8,
  });
  const [locations, setLocations] = useState<LocationsList | undefined>();
  const [refreshSearch, setRefreshSearch] = useState<boolean>(false);
  const { user } = useUser();
  const userInfo = user as UserInfo;
  const router = useRouter();
  const { data: emailOptions } = useSWR<mailOption[]>(
    '/api/user/getMailOptions'
  );
  const { mutate } = useSWRConfig();
  const Classic_URL = getConfirmedClassicURL(hostName);

  useEffect(() => {
    async function getLocation() {
      const res = await fetch('/api/locations/search', {
        body: JSON.stringify(locationParams),
        method: 'POST',
      });
      console.log('locationParams', locationParams);
      const locations: LocationsList | undefined = await res.json();
      console.log('res', locations);
      console.log('userInfo', userInfo);
      setLocations(locations);
    }
    getLocation();
  }, [locationParams, refreshSearch]);

  pages = settingsPages;

  //Parse page from url
  selectedPage = pages?.find((sp) => sp.key === router.query.settingsPage);

  //If query does not match a setting page, send user to account
  if (!selectedPage) selectedPage = pages?.find((sp) => sp.key === 'account');

  const isSelected = (page: string): boolean => {
    //Route user back to classic pages
    if (selectedPage?.key === 'templates')
      window.location.href = Classic_URL + '/user/preFilledOffers';
    if (selectedPage?.key === 'integrations')
      window.location.href = Classic_URL + '/user/emailOptions';
    return selectedPage?.key === page ? true : false;
  };

  return (
    <Layout>
      {
        <div className="flex flex-col">
          <div className=" flex flex-row px-8">
            <div>
              <h1 className="text-5xl">Basic Settings</h1>
              {/* Using term "basic setttings" until all settings are ported over */}
              {/* <h1 className="text-5xl">{selectedPage?.name}</h1> */}
            </div>
          </div>
          <div className="ml-16"></div>
          <div>
            <div className="flex flex-col p-8 m-4 lg:flex-row lg:p-0">
              <div className="lg:w-1/6 w-full">
                <SettingsSidebar
                  pages={settingsPages}
                  selectedPage={selectedPage}
                />
              </div>
              <div
                className={`lg:w-5/6 w-full ${
                  isSelected('account') ? '' : 'hidden'
                }`}
              >
                <Account timeZones={timeZones} classicUrl={Classic_URL} />
              </div>
              <div
                className={`lg:w-5/6 w-full ${
                  isSelected('notifications') ? '' : 'hidden'
                }`}
              >
                <Notifications
                  userInfo={userInfo}
                  emailOptions={emailOptions}
                  mutateMailOptions={mutate}
                />
              </div>
              <div
                className={`lg:w-5/6 w-full ${
                  isSelected('locations') ? '' : 'hidden'
                }`}
              >
                <Locations
                  userInfo={userInfo}
                  locations={locations}
                  locationParams={locationParams}
                  setLocationParams={setLocationParams}
                  setRefreshSearch={setRefreshSearch}
                  refreshSearch={refreshSearch}
                />
              </div>
            </div>
          </div>
        </div>
      }
    </Layout>
  );
};

//No header until next gen is fully released (big bang)
// Settings.getLayout = function (page: ReactElement) {
//   return <Layout>{page}</Layout>;
// };

//Requires User to be signed-in to use the Settings page
const protectedSettings = withPageAuthRequired(Settings);

export default protectedSettings;

export async function getServerSideProps(context: any) {
  const { req } = context;
  //Fetch timezones data from NextJS server
  const tzRes = await fetch(`${process.env.BASE_URL}/api/timezones`);
  const timeZones = await tzRes.json();

  const hostName = req.headers.host;

  return { props: { timeZones, hostName } };
}
